import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router';

import { config } from './config';
import { main } from './configuration';
import { store } from './configuration/setup/store';
import { handleLoginRedirect } from './configuration/login/redirect';
import type { User } from 'oidc-client-ts';
import { InvalidLogin } from './features/app/InvalidLogin';
import { router } from './routes/Router';
import { IntlProvider } from 'react-intl';
import { DEFAULT_LOCALE } from './configuration/lang/lang';
import ErrorFallback from './components/ErrorFallback';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useDisplayMessages, useLocale } from './configuration/lang/langSlice';

const App = () => {
    const userLocale = useLocale();
    const displayMessages = useDisplayMessages();

    if (!(displayMessages && userLocale)) {
        return null;
    }

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ErrorBoundary fallback={<ErrorFallback />}>
                <RouterProvider router={router} />
            </ErrorBoundary>
        </IntlProvider>
    );
};

const renderApplication = ({ user }: { user: User | null }) => {
    const container = document.getElementById('root');
    // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
    const root = createRoot(container!);

    let showApp = true;
    try {
        // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
        showApp = user!.profile.sub.startsWith('azure-my-rio-de:');
    } catch (e) {
        console.error(e);
    }

    if (showApp) {
        root.render(
            <Provider store={store}>
                <App />
            </Provider>
        );
    } else {
        root.render(<InvalidLogin idToken={user?.id_token} />);
    }
};

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;

if ((isDev && config.enableMockServer) || isProdPreview) {
    import('../mocks/browser').then(({ startWorker }) => startWorker()).then(() => main(renderApplication));
}

if (window.location.href.startsWith(config.login.redirectUri as string)) {
    handleLoginRedirect();
} else if (isProd && !isProdPreview) {
    void main(renderApplication);
}
